import { Link } from 'react-router-dom';
import { useScrollReveal, useScrollRevealGroup } from '@/hooks/useScrollReveal';

const products = [
  {
    title: 'Telegram заявки',
    icon: 'ri-telegram-line',
    description: 'Мониторинг 200 000+ чатов. Система находит людей с коммерческими запросами и передаёт их вам.',
    features: [
      'Поиск спроса в чатах',
      'Анализ сообщений',
      'Определение намерения покупки',
      'Выдача горячих заявок',
    ],
    stat: 'от 99 ₽ за заявку',
    to: '/telegram-leads',
  },
  {
    title: 'AI рассыльщик',
    icon: 'ri-robot-2-line',
    description: 'Автоматические диалоги с клиентами через ваши мессенджеры. AI пишет первым, квалифицирует, передаёт в CRM.',
    features: [
      'Пишет первым',
      'Отвечает 24/7',
      'Ведёт осмысленный диалог',
      'Квалифицирует заявки',
      'Передаёт в CRM',
    ],
    stat: 'от 990 ₽ / мес',
    to: '/ai-sales',
  },
  {
    title: 'Перехват конкурентов',
    icon: 'ri-crosshair-line',
    description: 'Находит клиентов, которые уже взаимодействуют с конкурентами и находятся в стадии покупки.',
    features: [
      'Анализ конкурентов',
      'Сбор аудитории с рынка',
      'Выявление горячего спроса',
      'Запуск AI-контакта',
    ],
    stat: 'от 20 000 ₽ за пакет',
    to: '/competitors',
  },
  {
    title: 'Интеграции',
    icon: 'ri-link-m',
    description: 'Все каналы продаж и CRM в единой системе. Заявки из Telegram, WhatsApp и MAX — в одну воронку.',
    features: [
      'Telegram / WhatsApp / MAX',
      'amoCRM и Bitrix24',
      'Единый поток заявок',
      'Сквозная аналитика',
    ],
    stat: 'Бесплатно',
    to: '/integrations',
  },
  {
    title: 'Заявки под ключ',
    icon: 'ri-double-quotes-r',
    description: 'Полный цикл: от поиска клиентов до передачи в CRM. Вы платите только за квалифицированных лидов.',
    features: [
      'Мониторинг 200 000+ чатов',
      'AI-диалоги и квалификация',
      'Передача готовых клиентов',
      'Персональный менеджер',
    ],
    stat: 'от 99 ₽ за заявку',
    to: '/leads-turnkey',
  },
];

export default function ProductsSection() {
  const { ref: labelRef, revealed: labelRevealed } = useScrollReveal();
  const { ref: titleRef, revealed: titleRevealed } = useScrollReveal({ delay: 150 });
  const { containerRef: cardsRef, revealedCount: cardsRevealed } = useScrollRevealGroup<HTMLDivElement>(5);

  return (
    <section className="py-20 md:py-28 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="mb-12 md:mb-16">
          <span
            ref={labelRef}
            className={`inline-block text-[12px] font-semibold uppercase tracking-[0.18em] text-primary-600 reveal-fade-up ${labelRevealed ? 'revealed' : ''}`}
          >
            Продукты
          </span>
          <h2
            ref={titleRef}
            className={`mt-4 font-display font-extrabold text-[34px] md:text-[46px] leading-[1.1] tracking-tightest text-foreground-950 max-w-2xl reveal-fade-up ${titleRevealed ? 'revealed' : ''}`}
          >
            Четыре инструмента. Одна система. Конкретный результат.
          </h2>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
          {products.map((product, idx) => (
            <Link
              key={product.to}
              to={product.to}
              className={`group flex flex-col sm:flex-row bg-background-50 rounded-2xl border border-background-200 hover:border-primary-500 transition-all duration-300 cursor-pointer overflow-hidden reveal-fade-up ${idx < cardsRevealed ? 'revealed' : ''}`}
              style={idx < cardsRevealed ? { transitionDelay: `${idx * 100}ms` } : undefined}
            >
              <div className="sm:w-[130px] lg:w-[150px] shrink-0 bg-background-100 flex items-center justify-center py-7 sm:py-0 border-b sm:border-b-0 sm:border-r border-background-200 group-hover:bg-primary-50 transition-colors duration-300 rounded-t-2xl sm:rounded-l-2xl sm:rounded-tr-none">
                <span className="w-12 h-12 rounded-lg bg-background-950 text-background-50 flex items-center justify-center group-hover:bg-primary-500 transition-colors duration-300">
                  <i className={`${product.icon} text-[22px]`}></i>
                </span>
              </div>

              <div className="p-6 md:p-7 flex flex-col justify-between flex-1 min-w-0">
                <div>
                  <h3 className="font-display font-bold text-[17px] text-foreground-950 mb-2">
                    {product.title}
                  </h3>
                  <p className="text-[14px] text-foreground-500 leading-relaxed mb-3">
                    {product.description}
                  </p>
                  <ul className="space-y-1.5 mb-4">
                    {product.features.map((feat) => (
                      <li key={feat} className="flex items-start gap-2 text-[14px] text-foreground-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-1.5 shrink-0" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-background-200">
                  <span className="text-[13px] font-semibold text-foreground-800">{product.stat}</span>
                  <span className="w-8 h-8 rounded-full bg-background-200 text-foreground-400 group-hover:bg-primary-500 group-hover:text-background-50 flex items-center justify-center transition-colors shrink-0">
                    <i className="ri-arrow-right-line text-sm" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}